import { ChangeEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Grid } from "@mui/material";
import { Redeem } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { addMonths, endOfMonth, startOfMonth, subMonths } from "date-fns";

import { formatItem } from "@framework/exchange";
import { StyledListWrapper } from "@framework/styled";
import { ClaimStatus, IReferralEvents, IReferralRewardShare } from "@framework/types";
import { Breadcrumbs, PageHeader } from "@dzambalaorg/mui-page-layout";
import { useApiCall, useFetchQuery } from "@dzambalaorg/react-hooks";
import { IPaginationResult } from "@dzambalaorg/types-collection";
import { Button } from "@dzambalaorg/ui-kit";

import { referralRewardColumnsCreator } from "./utils";

export enum RefClaimStatus {
  NEW = "NEW",
  CREATED = "CREATED",
  CLAIMED = "CLAIMED",
}

export const ReferralReward = () => {
  const { isLoading, take, skip, onChangePage, allRows, totalCount, refetch } = useFetchQuery<
    IPaginationResult<IReferralEvents>
  >("/referral/reward", void 0, void 0, {
    startTimestamp: startOfMonth(subMonths(new Date(), 1)).toISOString(),
    endTimestamp: endOfMonth(addMonths(new Date(), 1)).toISOString(),
  });

  const { formatMessage } = useIntl();

  const columns = referralRewardColumnsCreator(formatMessage);

  const { fn: handleClaimApi } = useApiCall(
    api =>
      api.fetchJson({
        url: "/referral/claim/create",
        method: "POST",
      }),
    { success: true, error: true },
  );

  const handleClaim = () => {
    return handleClaimApi(void 0).then(() => {
      void refetch();
    });
  };

  const getClaimStatus = (share: IReferralRewardShare | null) => {
    if (share?.claim) {
      const { claim } = share;
      switch (claim.claim?.claimStatus) {
        case ClaimStatus.NEW:
          return RefClaimStatus.CREATED;
        case ClaimStatus.REDEEMED:
          return RefClaimStatus.CLAIMED;
        default:
          return RefClaimStatus.CREATED;
      }
    } else {
      return RefClaimStatus.NEW;
    }
  };

  const noRows = !(allRows.length > 0);
  const noNewClaims =
    allRows
      .filter(row => (row.shares ? row.shares[0] : null))
      .map(row => row.shares)
      .filter(shares => getClaimStatus(shares ? shares[0] : null) === RefClaimStatus.NEW).length === 0;

  const disabledClaim = noRows || noNewClaims;

  return (
    <Grid>
      <Breadcrumbs path={["dashboard", "referral", "referral.reward"]} />

      <PageHeader message="pages.referral.reward.title">
        <Button startIcon={<Redeem />} onClick={handleClaim} disabled={disabledClaim} data-testid="ClaimButton">
          <FormattedMessage id={"form.buttons.claim"} />
        </Button>
      </PageHeader>
      <StyledListWrapper count={allRows.length} isLoading={isLoading}>
        <DataGrid
          pagination
          paginationMode="server"
          rowCount={totalCount}
          paginationModel={{ page: skip / take, pageSize: take }}
          onPaginationModelChange={({ page }) => onChangePage({} as ChangeEvent<unknown>, page)}
          pageSizeOptions={[5, 10, 25, 100]}
          loading={isLoading}
          columns={columns}
          rows={allRows.map((reward: IReferralEvents, idx) => ({
            id: idx,
            merchant: reward.merchant!.title,
            account: reward.account,
            item: formatItem(reward.item),
            price: formatItem(reward.price),
            share: reward.shares ? `${reward.shares[0].share / 100}%` : `0%`,
            createdAt: reward.createdAt,
            event: reward.history!.parent?.eventType,
            claim: getClaimStatus(reward.shares ? reward.shares[0] : null),
          }))}
        />
      </StyledListWrapper>
    </Grid>
  );
};
